import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { UserLanguage } from "../contexts/LanguageContext";

// Einführungs-Tour für Mitarbeiter- und Admin-Bereich. Öffnet sich beim
// allerersten Login automatisch (Layout.tsx / AdminLayout.tsx, Merker im Profil)
// und jederzeit über das „?" in der Kopfleiste. Die Schritte zeigen auf Elemente
// mit data-tour="…"; fehlt ein Ziel oder ist es unsichtbar, steht die Karte
// mittig. Optionale Schritte (Kacheln, die nicht jeder hat) werden dann
// übersprungen. Beide Layouts öffnen die Tour auf ihrem Dashboard.
// Texte hier im Datensatz statt in translations.ts; die Admin-Tour ist wie der
// ganze Admin-Bereich nur deutsch (adminTourSteps.ts).

export type Bilingual = { de: string; en?: string };

export type TourStep = {
  id: string;
  target?: string;
  /** Sidebar für diesen Schritt aufklappen, damit die Beschriftung sichtbar ist. */
  sidebar?: boolean;
  /** Ohne sichtbares Ziel überspringen statt mittig anzeigen. */
  optional?: boolean;
  /** Nur zeigen, wenn das Paket dieses Feature enthält (Filter im Layout). */
  feature?: string;
  title: Bilingual;
  body: Bilingual[];
};

export const EMPLOYEE_TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: { de: "Willkommen bei SHERLOX AWARE", en: "Welcome to SHERLOX AWARE" },
    body: [
      {
        de: "Die meisten Cyberangriffe beginnen nicht mit Technik, sondern mit einer Mail, einem Anruf oder einem schnellen Klick. Hier lernst du in kurzen Modulen, solche Situationen zu erkennen und richtig zu reagieren – für dich und für dein Unternehmen.",
        en: "Most cyberattacks don't start with technology but with an email, a phone call or a quick click. Here you'll learn in short modules how to spot these situations and respond the right way – for yourself and for your company.",
      },
      {
        de: "Diese kurze Tour zeigt dir in einer Minute, wo du was findest.",
        en: "This short tour shows you where everything is in about a minute.",
      },
    ],
  },
  {
    id: "nav",
    target: '[data-tour="nav"]',
    sidebar: true,
    title: { de: "Die Navigation", en: "Navigation" },
    body: [
      {
        de: "Links findest du alle Bereiche. Am Computer klappt die Leiste auf, sobald du mit der Maus darüberfährst – auf dem Handy öffnest du sie über das Menü-Symbol oben links.",
        en: "All areas are on the left. On a computer the bar expands when you hover over it – on a phone, open it with the menu icon at the top left.",
      },
    ],
  },
  {
    id: "dashboard",
    target: '[data-tour="nav-dashboard"]',
    sidebar: true,
    title: { de: "Mein Dashboard", en: "My dashboard" },
    body: [
      {
        de: "Deine Startseite – hier siehst du jederzeit, wo du stehst. Schauen wir sie uns Stück für Stück an.",
        en: "Your home page – it always shows where you stand. Let's go through it piece by piece.",
      },
    ],
  },
  {
    id: "dash-status",
    target: '[data-tour="dash-status"]',
    optional: true,
    title: { de: "Die Ampel", en: "The traffic light" },
    body: [
      {
        de: "Ein Satz, der sofort sagt, wo du stehst: Grün heißt alles erledigt, Gelb heißt bald ist etwas fällig, Rot heißt es ist etwas offen oder abgelaufen.",
        en: "One sentence that tells you right away where you stand: green means everything's done, yellow means something is due soon, red means something is open or expired.",
      },
      {
        de: "Ist etwas zu tun, bringt dich der Button daneben direkt zum nächsten Modul.",
        en: "If there's something to do, the button next to it takes you straight to the next module.",
      },
    ],
  },
  {
    id: "dash-stats",
    target: '[data-tour="dash-stats"]',
    optional: true,
    title: { de: "Deine Zahlen", en: "Your numbers" },
    body: [
      {
        de: "Trainings-Fortschritt: wie viele deiner Module in der aktuellen Runde erledigt sind. Ø Quiz-Score: dein Durchschnitt über alle Quizze. Trainings gesamt: alle bestandenen Trainings seit deinem Start – Wiederholungen zählen mit.",
        en: "Training progress: how many of your modules are done in the current round. Average quiz score: your average across all quizzes. Total trainings: every training you've passed since you started – repeats included.",
      },
    ],
  },
  {
    id: "dash-open",
    target: '[data-tour="dash-open"]',
    optional: true,
    title: { de: "Offen", en: "Open" },
    body: [
      {
        de: "Was du noch erledigen musst, sortiert nach Themenbereichen. Ein Klick auf ein Thema öffnet es im Trainingsbereich.",
        en: "What you still need to do, sorted by topic area. Click a topic to open it in the training area.",
      },
    ],
  },
  {
    id: "dash-completed",
    target: '[data-tour="dash-completed"]',
    optional: true,
    title: { de: "Abgeschlossen", en: "Completed" },
    body: [
      {
        de: "Deine Nachweise mit Datum und Gültigkeit. Läuft ein Modul in diesem Jahr ab, steht es zusätzlich oben unter „Offen“ – so verpasst du keine Auffrischung.",
        en: "Your records with date and validity. If a module expires this year, it also appears above under “Open” – so you never miss a refresher.",
      },
    ],
  },
  {
    id: "dash-radar",
    target: '[data-tour="dash-radar"]',
    optional: true,
    title: { de: "Dein Trainingsnetz", en: "Your training web" },
    body: [
      {
        de: "Jeder Faden steht für ein Thema. Je fester und grüner das Netz, desto besser bist du abgesichert. Orange heißt: läuft bald ab, Rot: fällig oder noch offen.",
        en: "Each thread stands for a topic. The tighter and greener the web, the better you're protected. Orange means expiring soon, red means due or still open.",
      },
      {
        de: "Oben rechts siehst du deinen Trainings-Fortschritt: wie viele deiner Module in der aktuellen Runde erledigt sind.",
        en: "Top right shows your training progress: how many of your modules are done in the current round.",
      },
    ],
  },
  {
    id: "dash-validity",
    target: '[data-tour="dash-validity"]',
    optional: true,
    title: { de: "Wie lange gilt ein Modul?", en: "How long is a module valid?" },
    body: [
      {
        de: "Sicherheitswissen veraltet – deshalb gelten abgeschlossene Module nur eine gewisse Zeit. Hier steht die Regel deines Unternehmens mit einem Beispiel ab heute.",
        en: "Security knowledge goes stale – that's why completed modules are only valid for a certain time. Here you'll find your company's rule with an example starting today.",
      },
    ],
  },
  {
    id: "training",
    target: '[data-tour="nav-lernmodule"]',
    sidebar: true,
    title: { de: "Trainingsbereich", en: "Training area" },
    body: [
      {
        de: "Hier liegen deine Lernmodule. Jedes Modul erklärt ein Thema an echten Beispielen, oft mit kurzem Video, und endet mit einem Quiz. Ist das Quiz bestanden, zählt das Modul als abgeschlossen.",
        en: "This is where your learning modules are. Each module explains a topic with real examples, often with a short video, and ends with a quiz. Once you pass the quiz, the module counts as completed.",
      },
      {
        de: "Fällige oder abgelaufene Module sind markiert – die wiederholst du einfach.",
        en: "Modules that are due or expired are marked – simply repeat them.",
      },
    ],
  },
  {
    id: "test",
    target: '[data-tour="nav-test"]',
    sidebar: true,
    title: { de: "Testbereich", en: "Test area" },
    body: [
      {
        de: "Im Wissenstest prüfst du dein Wissen über alle Themen hinweg. Ab 80 % hast du bestanden.",
        en: "The knowledge test checks what you know across all topics. You pass with 80 % or more.",
      },
      {
        de: "Sind alle zugewiesenen Module und der Wissenstest geschafft, steht dein Abschlusszertifikat bereit.",
        en: "Once all assigned modules and the knowledge test are done, your completion certificate is ready.",
      },
    ],
  },
  {
    id: "manager",
    target: '[data-tour="nav-manager"]',
    sidebar: true,
    title: { de: "Manager-Bereich", en: "Manager area" },
    body: [
      {
        de: "Weil du Mitarbeitende führst, siehst du hier den Schulungsstand deines Teams und kannst gezielt an offene Module erinnern.",
        en: "Because you lead a team, you can see your team's training status here and send targeted reminders about open modules.",
      },
    ],
  },
  {
    id: "settings",
    target: '[data-tour="nav-einstellungen"]',
    sidebar: true,
    title: { de: "Einstellungen", en: "Settings" },
    body: [
      {
        de: "Hier stellst du Darstellung, Sprache und E-Mail-Benachrichtigungen ein.",
        en: "Adjust appearance, language and email notifications here.",
      },
    ],
  },
  {
    id: "notifications",
    target: '[data-tour="notifications"]',
    title: { de: "Benachrichtigungen", en: "Notifications" },
    body: [
      {
        de: "Die Glocke meldet sich, wenn ein Modul bald abläuft, ein Quiz knapp war oder deine Führungskraft dich an etwas erinnert. Ein Klick bringt dich direkt an die richtige Stelle.",
        en: "The bell lights up when a module is about to expire, a quiz was close or your manager sends you a reminder. One click takes you straight to the right place.",
      },
    ],
  },
  {
    id: "theme",
    target: '[data-tour="theme"]',
    title: { de: "Hell oder dunkel", en: "Light or dark" },
    body: [
      {
        de: "Mit diesem Schalter wechselst du jederzeit zwischen hellem und dunklem Design.",
        en: "Use this switch to toggle between light and dark design at any time.",
      },
    ],
  },
  {
    id: "help",
    target: '[data-tour="help"]',
    title: { de: "Hilfe jederzeit", en: "Help anytime" },
    body: [
      {
        de: "Über das Fragezeichen öffnest du diese Tour jederzeit wieder.",
        en: "Click the question mark to open this tour again at any time.",
      },
    ],
  },
  {
    id: "finish",
    title: { de: "Bereit für dein erstes Modul?", en: "Ready for your first module?" },
    body: [
      {
        de: "Am besten startest du direkt im Trainingsbereich. Und denk dran: Im Zweifel lieber einmal nachfragen, bevor du klickst.",
        en: "The best place to start is the training area. And remember: when in doubt, ask once more before you click.",
      },
    ],
  },
];

const UI = {
  next: { de: "Weiter", en: "Next" },
  back: { de: "Zurück", en: "Back" },
  skip: { de: "Tour überspringen", en: "Skip tour" },
  done: { de: "Fertig", en: "Done" },
  step: { de: "Schritt {n} von {total}", en: "Step {n} of {total}" },
  close: { de: "Schließen", en: "Close" },
};

const CARD_WIDTH = 360;
const GAP = 14;
const EDGE = 12;
const SPOT_PAD = 6;

type Rect = { top: number; left: number; width: number; height: number };

function measure(selector: string | undefined): Rect | null {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null;
  if (r.right < 0 || r.bottom < 0 || r.left > window.innerWidth || r.top > window.innerHeight) return null;
  // Hohe Bereiche (z. B. „Offen" mit vielen Themen) auf den sichtbaren Teil kappen.
  const top = Math.max(r.top - SPOT_PAD, 4);
  const bottom = Math.min(r.bottom + SPOT_PAD, window.innerHeight - 4);
  return { top, left: r.left - SPOT_PAD, width: r.width + SPOT_PAD * 2, height: Math.max(bottom - top, 0) };
}

function scrollTargetIntoView(selector: string | undefined): void {
  if (!selector) return;
  const el = document.querySelector(selector);
  if (!el) return;
  const r = el.getBoundingClientRect();
  if (r.top >= 70 && r.bottom <= window.innerHeight - 20) return;
  // Hohe Ziele oben anlegen, damit Überschrift und Anfang sichtbar sind.
  el.scrollIntoView({ block: r.height > window.innerHeight * 0.6 ? "start" : "center", behavior: "smooth" });
}

// Karte bevorzugt rechts vom Ziel (Sidebar), sonst darunter, sonst darüber.
function placeCard(spot: Rect | null, cardWidth: number, cardHeight: number): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampLeft = (x: number) => Math.min(Math.max(x, EDGE), vw - cardWidth - EDGE);
  const clampTop = (y: number) => Math.min(Math.max(y, EDGE), Math.max(EDGE, vh - cardHeight - EDGE));
  if (!spot) return { top: clampTop((vh - cardHeight) / 2), left: clampLeft((vw - cardWidth) / 2) };

  const right = spot.left + spot.width + GAP;
  if (right + cardWidth + EDGE <= vw) {
    return { top: clampTop(spot.top + spot.height / 2 - cardHeight / 2), left: right };
  }
  const below = spot.top + spot.height + GAP;
  if (below + cardHeight + EDGE <= vh) {
    return { top: below, left: clampLeft(spot.left + spot.width / 2 - cardWidth / 2) };
  }
  const above = spot.top - GAP - cardHeight;
  if (above >= EDGE) {
    return { top: above, left: clampLeft(spot.left + spot.width / 2 - cardWidth / 2) };
  }
  // Ziel füllt fast den Bildschirm: Karte unten rechts darüberlegen.
  return { top: clampTop(vh - cardHeight - EDGE * 2), left: clampLeft(vw - cardWidth - EDGE * 2) };
}

interface AppTutorialProps {
  open: boolean;
  language: UserLanguage;
  steps: TourStep[];
  finishLabel: Bilingual;
  onClose: () => void;
  onFinish: () => void;
  /** Layout klappt die Sidebar auf/zu, solange ein Navigationsschritt aktiv ist. */
  onSidebarForce: (open: boolean) => void;
}

export default function AppTutorial({ open, language, steps, finishLabel, onClose, onFinish, onSidebarForce }: AppTutorialProps) {
  const tr = (text: Bilingual) => (language === "en" && text.en ? text.en : text.de);
  const [index, setIndex] = useState(0);
  const directionRef = useRef<1 | -1>(1);
  const [spot, setSpot] = useState<Rect | null>(null);
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { if (open) { directionRef.current = 1; setIndex(0); } }, [open]);

  const step = steps[Math.min(index, steps.length - 1)];
  const isLast = index >= steps.length - 1;

  useEffect(() => {
    if (!open) { onSidebarForce(false); return; }
    onSidebarForce(step.sidebar === true);
  }, [open, step, onSidebarForce]);

  useEffect(() => () => onSidebarForce(false), [onSidebarForce]);

  // Position nachführen: während die Sidebar aufklappt (160 ms), die Seite zum
  // Ziel scrollt und das Dashboard noch Daten nachlädt.
  useLayoutEffect(() => {
    if (!open) return;
    let frame = 0;
    let scrolled = false;
    const started = performance.now();
    setSpot(null);
    setCardPos(null);
    const update = () => {
      const nextSpot = measure(step.target);
      if (step.optional && !nextSpot) return;
      setSpot(nextSpot);
      const card = cardRef.current;
      const width = Math.min(CARD_WIDTH, window.innerWidth - EDGE * 2);
      setCardPos(placeCard(nextSpot, width, card ? card.offsetHeight : 220));
    };
    const loop = () => {
      const elapsed = performance.now() - started;
      if (!scrolled && document.querySelector(step.target ?? ":not(*)")) {
        scrolled = true;
        scrollTargetIntoView(step.target);
      }
      update();
      if (step.optional && !document.querySelector(step.target ?? ":not(*)") && elapsed > 900) {
        // Kachel gibt es für diese Person nicht – in Laufrichtung weiter.
        setIndex(i => Math.min(Math.max(i + directionRef.current, 0), steps.length - 1));
        return;
      }
      if (elapsed < 900) frame = requestAnimationFrame(loop);
    };
    loop();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, step, steps.length]);

  useEffect(() => { if (open) primaryRef.current?.focus(); }, [open, index]);

  const next = useCallback(() => { directionRef.current = 1; setIndex(i => Math.min(i + 1, steps.length - 1)); }, [steps.length]);
  const back = useCallback(() => { directionRef.current = -1; setIndex(i => Math.max(i - 1, 0)); }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowRight" && !isLast) { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); back(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isLast, next, back, onClose]);

  if (!open) return null;

  const cardWidth = Math.min(CARD_WIDTH, window.innerWidth - EDGE * 2);
  const btnBase = {
    padding: "0.5rem 0.95rem",
    borderRadius: "8px",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000 }}>
      {/* Klicks auf die Seite darunter abfangen; die Tour schließt nur bewusst. */}
      <div style={{ position: "absolute", inset: 0, background: spot ? "transparent" : "rgba(10,15,30,0.6)" }} />
      {spot && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
            borderRadius: "10px",
            boxShadow: "0 0 0 2px #F97316, 0 0 0 9999px rgba(10,15,30,0.6)",
            transition: "top 180ms ease, left 180ms ease, width 180ms ease, height 180ms ease",
            pointerEvents: "none",
          }}
        />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-tutorial-title"
        style={{
          position: "fixed",
          top: cardPos?.top ?? "50%",
          left: cardPos?.left ?? "50%",
          width: cardWidth,
          boxSizing: "border-box",
          background: "var(--bg-card)",
          color: "var(--text-primary)",
          border: "1px solid var(--border)",
          borderRadius: "14px",
          boxShadow: "0 18px 48px rgba(0,0,0,0.28)",
          padding: "1.15rem 1.2rem 1rem",
          visibility: cardPos ? "visible" : "hidden",
          transition: "top 180ms ease, left 180ms ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", marginBottom: "0.6rem" }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.03em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            {tr(UI.step).replace("{n}", String(index + 1)).replace("{total}", String(steps.length))}
          </span>
          <button
            onClick={onClose}
            aria-label={tr(UI.close)}
            title={tr(UI.close)}
            style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: "2px", display: "flex" }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {step.id === "welcome" && (
          <div style={{ width: "44px", height: "44px", background: "#fff", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.15)", marginBottom: "0.75rem" }}>
            <img src="/brand/aware-icon.png" alt="" width="36" height="36" style={{ display: "block", objectFit: "contain" }} />
          </div>
        )}

        <h2 id="app-tutorial-title" style={{ margin: "0 0 0.5rem", fontSize: "1.05rem", fontWeight: 700, color: "var(--text-primary)" }}>
          {tr(step.title)}
        </h2>
        {step.body.map((p, i) => (
          <p key={i} style={{ margin: "0 0 0.55rem", fontSize: "0.875rem", lineHeight: 1.55, color: "var(--text-secondary)" }}>
            {tr(p)}
          </p>
        ))}

        <div style={{ display: "flex", gap: "5px", justifyContent: "center", margin: "0.8rem 0 0.9rem" }}>
          {steps.map((s, i) => (
            <span
              key={s.id}
              style={{
                width: i === index ? "18px" : "6px",
                height: "6px",
                borderRadius: "3px",
                background: i === index ? "#1B6AB1" : "var(--border)",
                transition: "width 160ms ease, background 160ms ease",
              }}
            />
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
          {isLast ? (
            <button onClick={onClose} style={{ ...btnBase, background: "transparent", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              {tr(UI.done)}
            </button>
          ) : index === 0 ? (
            <button onClick={onClose} style={{ ...btnBase, background: "transparent", border: "none", color: "var(--text-muted)", padding: "0.5rem 0.25rem", fontWeight: 500 }}>
              {tr(UI.skip)}
            </button>
          ) : (
            <button onClick={back} style={{ ...btnBase, background: "transparent", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              {tr(UI.back)}
            </button>
          )}
          <button
            ref={primaryRef}
            onClick={isLast ? onFinish : next}
            style={{ ...btnBase, background: "#1B6AB1", border: "1px solid #1B6AB1", color: "#fff" }}
          >
            {isLast ? tr(finishLabel) : `${tr(UI.next)} →`}
          </button>
        </div>
      </div>
    </div>
  );
}
